// apps/vendor/src/features/announcements/api.ts
//
// Vendor-portal announcement feed — parity with merchant-v3's
// hooks/useAnnouncements.ts. Fetches active announcements from the public
// endpoint `GET /api/v1/announcements?portal=vendor&surface=...&category=...`.
//
// The vendor SPA previously shipped NO announcement surface at all
// (VendorAppShell comment: "no announcement banner"), so admin banners
// targeting the `vendor` portal never rendered. This hook + the sibling
// AnnouncementBanner/AnnouncementCarousel components close that gap.
//
// Dismiss state is per-announcement localStorage (parity with the other
// portals — there is no BE per-user dismiss endpoint); `force_show` bypasses
// the dismiss so a critical banner cannot be silenced.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { csrfFetch } from '../../lib/api/csrfFetch';

export type AnnouncementSurface =
  | 'topbar'
  | 'page:orders'
  | 'page:dashboard'
  | 'page:products'
  | 'login'
  | 'homepage';

export type AnnouncementCategory =
  | 'system_notice'
  | 'promo_marketing'
  | 'maintenance'
  | 'feature_release';

export type AnnouncementPriority = 'low' | 'medium' | 'high' | 'critical';

export interface AnnouncementI18nString {
  en: string;
  vi?: string;
  fr?: string;
}

export interface Announcement {
  id: string;
  title_i18n: AnnouncementI18nString;
  body_i18n: AnnouncementI18nString;
  category: AnnouncementCategory;
  surface: AnnouncementSurface[];
  priority: AnnouncementPriority;
  schedule_start: string | null;
  schedule_end: string | null;
  dismissible: boolean;
  force_show: boolean;
}

interface ApiOk<T> {
  ok: true;
  data: T;
}
interface ApiErr {
  ok: false;
  error: { code: string; message: string };
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) {
    const err = new Error(body.error?.message ?? 'Request failed') as Error & {
      code?: string;
    };
    err.code = body.error?.code;
    throw err;
  }
  return body.data;
}

export interface UseAnnouncementsParams {
  surface: AnnouncementSurface;
  category?: AnnouncementCategory;
}

export function useAnnouncements(
  params: UseAnnouncementsParams,
): UseQueryResult<Announcement[]> {
  const { surface, category } = params;
  return useQuery({
    queryKey: ['vendor', 'announcements', surface, category ?? null],
    queryFn: async (): Promise<Announcement[]> => {
      const qs = new URLSearchParams({ portal: 'vendor', surface });
      if (category) qs.set('category', category);
      const res = await csrfFetch(`/api/v1/announcements?${qs.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      const rows = await unwrap<Announcement[]>(res);
      return Array.isArray(rows) ? rows : [];
    },
    staleTime: 60_000,
    retry: 1,
  });
}

/** Per-announcement localStorage dismiss state. Key: `announcement.{id}.dismissed`. */
export function isAnnouncementDismissed(id: string): boolean {
  try {
    return localStorage.getItem(`announcement.${id}.dismissed`) === '1';
  } catch {
    return false;
  }
}

export function dismissAnnouncement(id: string): void {
  try {
    localStorage.setItem(`announcement.${id}.dismissed`, '1');
  } catch {
    /* localStorage unavailable in private mode — fail silent */
  }
}
